import React, { useEffect, useMemo, useRef, useState } from "react";
import { gsap } from "gsap";
import {
    formatCountingStat,
    parseStatValue,
    visibleStatText,
} from "./formatStatValue";

export default function AnimatedTextDesc({
    result,
    description,
    textOnly = false,
}: {
    result?: string | null;
    description?: string | null;
    textOnly?: boolean;
}) {
    const stat = visibleStatText(result);
    const label = visibleStatText(description);
    const parts = useMemo(
        () => (!textOnly && stat ? parseStatValue(stat) : null),
        [stat, textOnly]
    );
    const [currentValue, setCurrentValue] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!parts) return;

        const node = ref.current;
        if (!node) return;

        const state = { value: 0 };
        let tween: gsap.core.Tween | null = null;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting || hasAnimated || tween) return;

                tween = gsap.to(state, {
                    value: parts.value,
                    duration: 2,
                    ease: "power1.inOut",
                    onUpdate: () => {
                        setCurrentValue(state.value);
                    },
                    onComplete: () => {
                        setCurrentValue(parts.value);
                        setHasAnimated(true);
                    },
                });
            },
            { threshold: 0.5 }
        );

        observer.observe(node);

        return () => {
            tween?.kill();
            observer.disconnect();
        };
    }, [parts, hasAnimated]);

    if (!stat && !label) return null;

    const displayed = stat
        ? parts
            ? formatCountingStat(
                  stat,
                  hasAnimated ? parts.value : currentValue,
                  hasAnimated
              )
            : stat
        : null;

    return (
        <div
            ref={ref}
            className="flex w-full min-w-0 flex-col items-center [container-type:inline-size]"
        >
            {textOnly && stat ? (
                <h3 className="w-full max-w-full text-balance px-1 text-center text-xl font-bold leading-tight text-purple md:max-w-[18rem] md:text-3xl md:leading-snug">
                    {stat}
                </h3>
            ) : displayed ? (
                <h3 className="max-w-full whitespace-nowrap text-center text-[clamp(1.5rem,22.5cqw,3.75rem)] font-bold leading-none text-purple md:text-[clamp(2rem,22.5cqw,6.555vw)]">
                    {displayed}
                </h3>
            ) : null}
            {label ? (
                <p className="w-full min-w-0 max-w-full text-balance px-1 text-center text-sm font-semibold leading-snug text-darkGrey md:max-w-[18rem] md:text-[1.333vw] md:leading-8">
                    {label}
                </p>
            ) : null}
        </div>
    );
}
